import React, { ReactNode, ReactElement } from 'react';
import { Trans, useTranslation, UseTranslationResponse } from 'react-i18next';
import { CustomTypeOptions } from 'i18next';

import { snakeToPascal } from '@utils/utils';
import { loadDefaultJapaneseParser, loadDefaultSimplifiedChineseParser } from 'budoux';
import { ApplicationLanguage } from '@/signals/locale';

import AkAnchor from '@components/dictionary-components/ak-anchor';
import AkBlack from '@components/dictionary-components/ak-black';
import AkBlue from '@components/dictionary-components/ak-blue';
import AkBold from '@components/dictionary-components/ak-bold';
import AkCyan from '@components/dictionary-components/ak-cyan';
import AkFatBlack from '@components/dictionary-components/ak-fat-black';
import AkGreyBg from '@components/dictionary-components/ak-grey-bg';
import AkGreenCheckmark from '@components/dictionary-components/ak-green-checkmark';
import AkItalic from '@components/dictionary-components/ak-italic';
import AkNoWrap from '@components/dictionary-components/ak-no-wrap';
import AkRed from '@components/dictionary-components/ak-red';
import AkYellowBg from '@components/dictionary-components/ak-yellow-bg';
import AkSmall from '@components/dictionary-components/ak-small';
import AkSmaller from '@components/dictionary-components/ak-smaller';
import Bullet from '@components/dictionary-components/bullet';
import { TranslationKey } from '../../typings/i18next';
import { unescape } from 'lodash-es';

import * as resources from '@i18n/locales/en-US';

/* eslint-disable no-restricted-syntax */
const components: Record<string, ReactElement> = {
    div: <div />,
    a: <AkAnchor />,
    b: <b />,
    br: <br />,
    figure: <figure />,
    i: <i />,
    img: <img />,
    p: <p />,
    s: <s />,
    u: <u />,
    strong: <strong />,
    em: <em />,
    code: <code />,
    h1: <h1 />,
    h2: <h2 />,
    h3: <h3 />,
    h4: <h4 />,
    h5: <h5 />,
    h6: <h6 />,
    ul: <ul />,
    ol: <ol />,
    li: <li />,
    table: <table />,
    caption: <caption />,
    tr: <tr />,
    th: <th />,
    td: <td />,

    AkBlack: <AkBlack />,
    AkSmall: <AkSmall />,
    AkSmaller: <AkSmaller />,
    AkBold: <AkBold />,
    AkBullet: <Bullet />,
    AkCheckmark: <AkGreenCheckmark />,
    AkCyan: <AkCyan />,
    AkFatBlack: <AkFatBlack />,
    AkBigAndBlack: <AkFatBlack />,
    AkGreyBg: <AkGreyBg />,
    AkItalic: <AkItalic />,
    AkNoWrap: <AkNoWrap />,
    AkRed: <AkRed />,
    AkBlue: <AkBlue />,
    AkYellow_bg: <AkYellowBg />,
};

type Primitive = string | number | boolean;
type Options = Record<string, ReactElement | Primitive>;

function toPascalCaseTag(key: string): string {
    if (key.startsWith('AK_')) {
        return (
            'Ak' +
            key
                .slice(3)
                .toLowerCase()
                .split('_')
                .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
                .join('')
        );
    }

    if (/^[A-Z][A-Za-z0-9]*$/.test(key)) {
        return key;
    }

    return key
        .toLowerCase()
        .split('_')
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join('');
}

export function hasReplacements(tags: string[], options: Record<string, any>): boolean {
    if (!tags?.length || !options) return false;

    const pascalOptionKeys = new Set(Object.keys(options).map(toPascalCaseTag));

    // Direct variable names (for {{variable}})
    const rawOptionKeys = new Set(Object.keys(options));

    return tags.every((tag) => pascalOptionKeys.has(tag) || rawOptionKeys.has(tag));

    return tags.some((tag) => pascalOptionKeys.has(tag) || rawOptionKeys.has(tag));
}

export function hasTagReplacements(tags: string[], options: Record<string, any>): boolean {
    if (!tags?.length) return false;

    const normalized = new Set(Object.keys(options).map(toPascalCaseTag));
    return tags.some((tag) => normalized.has(tag));
}

export function extractRequirements(str: string): string[] {
    if (typeof str !== 'string') return [];

    const found = new Set<string>();

    //
    // --- TAGS ---------------------------------------------------------
    //
    const tagRegex = /<\s*\/?\s*([A-Za-z][A-Za-z0-9:_-]*)[^>]*>/g;
    let tagMatch: RegExpExecArray | null;

    while ((tagMatch = tagRegex.exec(str)) !== null) {
        let tagName = tagMatch[1];

        // Normalize to PascalCase (AkButton, MyTag, etc.)
        const normalized = tagName.startsWith('Ak') // already our PascalCase AK tags
            ? tagName
            : tagName.charAt(0).toUpperCase() + tagName.slice(1);

        found.add(normalized);
    }

    //
    // --- VARIABLES -----------------------------------------------------
    //
    const varRegex = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*}}/g;
    let varMatch: RegExpExecArray | null;

    while ((varMatch = varRegex.exec(str)) !== null) {
        const variableName = varMatch[1]; // Keep variable names as-is
        found.add(variableName);
    }

    return Array.from(found);
}

export function extractTags(str: string): string[] {
    if (typeof str !== 'string') return [];

    const regex = /<\s*\/?\s*([A-Za-z][A-Za-z0-9:_-]*)[^>]*>/g;

    const found = new Set<string>();
    let match: RegExpExecArray | null;

    while ((match = regex.exec(str)) !== null) {
        const tagName = match[1];

        const normalized = tagName.startsWith('Ak')
            ? tagName // Already PascalCase
            : tagName.charAt(0).toUpperCase() + tagName.slice(1);

        found.add(normalized);
    }

    return Array.from(found);
}

function interpolateRaw(str: string, options: any) {
    return fixSpecialCharacters(str).replace(
        /\{\{\s*([A-Za-z_]\w*(?:\.[A-Za-z_]\w*)*)\s*}}/g,
        (match, path) => {
            const parts = path.split('.');
            let val = options;

            for (const p of parts) {
                if (val && typeof val === 'object' && p in val) {
                    val = val[p];
                } else {
                    return match;
                }
            }

            if (val === undefined || val === null) {
                return match;
            }

            return String(val);
        }
    );
}

function interpolationControl(raw: string, options: Options) {
    const interpolated = interpolateRaw(raw, options);

    const hasHtmlTags = /<\s*\/?\s*[A-Za-z][A-Za-z0-9:_-]*[^>]*>/.test(interpolated);
    const hasCustomTags = /<\s*\/?\s*Ak[A-Za-z0-9_-]*[^>]*>/.test(interpolated);

    return {
        interpolated,
        hasTags: hasHtmlTags || hasCustomTags,
    };
}

function flattenLeafKeys(obj: any, prefix = ''): string[] {
    return Object.entries(obj).flatMap(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'string' || typeof value === 'number') {
            return [path];
        }

        if (value && typeof value === 'object') {
            return flattenLeafKeys(value, path);
        }

        return [];
    });
}

export function fixSpecialCharacters(strToFix: string) {
    // Note: the replacement ' ' below is the special NBSP space character
    // Note: the replacement '–' below is the special NDASH special character

    return strToFix
        .replaceAll(/&#039;/gi, "'")
        .replaceAll(/&nbsp;/gi, ' ')
        .replaceAll(/&ndash;/gi, '–')
        .replaceAll(/&tm;/gi, '™')
        .replaceAll(/&reg;/gi, '®');
}

export function buildKeySet() {
    const allKeys: string[] = [];

    for (const [ns, content] of Object.entries(resources)) {
        const leafKeys = flattenLeafKeys(content);
        for (const k of leafKeys) {
            allKeys.push(`${ns}:${k}`);
        }
    }

    return new Set(allKeys);
}
export const ALL_KEYS = buildKeySet();

// -------------------------------------
// Detect languages for BudouX
// -------------------------------------
const selectParser = () => {
    const lang = ApplicationLanguage.value;
    if (lang === 'ja') return loadDefaultJapaneseParser();
    if (lang === 'zh') return loadDefaultSimplifiedChineseParser();
    return { parse: (str: string) => [str] };
};

// -------------------------------------
// Converts <AK_SOME_TAG> to <AkSomeTag> inside translation strings
// -------------------------------------
export function convertAkTagsToPascal(input: string): string {
    return input.replace(/<\/?AK_[A-Z0-9_:-]+[^>]*>/g, (tag) => {
        return tag.replace(/AK_([A-Z0-9_:-]+)/, (_, inner) => {
            const pascal = snakeToPascal(`_${inner}`.replace(/[:\-]/g, '_'));
            return `Ak${pascal}`;
        });
    });
}

export function convertTagsToPascal(input: string): string {
    return input.replace(/<\/?\s*([A-Za-z0-9_:-]+)[^>]*>/g, (tag, fullName) => {
        return tag.replace(fullName, () => {
            const normalized = fullName.replace(/[:\-]/g, '_');
            return snakeToPascal(normalized);
        });
    });
}
const parser = selectParser();

// -------------------------------------
// Types
// -------------------------------------
type Ns = keyof CustomTypeOptions['resources'];

export type UseTransReturn = UseTranslationResponse<Ns, string> & {
    t: <K extends TranslationKey>(
        key: K,
        options?: Record<string, any> & { returnObjects?: boolean }
    ) => any;
};

const parseChildren = (things: any) => {
    return things?.map((c: any) => {
        if (typeof c === 'string') {
            return parser.parse(c).map((s, idx) => {
                const parts = s.split('\n');
                return (
                    <React.Fragment key={idx}>
                        {parts.map((part, subIdx) => (
                            <React.Fragment key={subIdx}>
                                {part && <span>{part}</span>}
                                {subIdx < parts.length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                );
            });
        }

        if (typeof c === 'object' && c.props) {
            return {
                ...c,
                props: {
                    ...c.props,
                    children: parseChildren(c.props.children),
                },
            };
        }

        return things;
    });
};

export function cleanKey(key: string): string {
    const idx = key.indexOf(':');
    return idx === -1 ? `common:${key}` : key;
}

export function useTrans(namespace?: string): UseTransReturn {
    const { t: origT, i18n, ...rest } = useTranslation(namespace as Ns | undefined);
    function t(key: string | ReactElement, options?: Options): ReactNode | object {
        /*
         *
         * Cases to support:
         *  - ReactNode
         *  - i18nKey
         *  - i18nKey contains html tags
         *  - i18nKey contains custom tags
         *  - i18nKey contains variables including count
         *  - string
         *  - string contains html tags
         *  - string contains custom tags
         *  - string contains variables including count
         *  - returns object
         *  - JA + ZH
         *
         *  1- isReactNode
         *  2- isArray
         *  3- isObject
         *  4- hasVariable w/wo replacement
         *  5- hasComponent w/wo replacement
         *  6- hasHtmlTags w/wo replacement
         *  7- alreadyString        -> return Trans
         *  8- simple translation   -> return Trans
         * */

        const extraComponents: Record<string, ReactElement> = {};
        for (const [k, v] of Object.entries(options || {})) {
            extraComponents[snakeToPascal(k)] = v as ReactElement;
        }
        const augmentedOptions = { ...options, ...components, ...extraComponents };

        const isReactElement =
            key && typeof key === 'object' && (key as any).$$typeof === Symbol.for('react.element');
        if (isReactElement) return key;

        const raw = origT(unescape(key), { returnObjects: true, ...augmentedOptions });

        if (Array.isArray(raw)) {
            return raw.map((_, index) => t(`${key}.${index}`, augmentedOptions));
        }

        if (typeof raw === 'object' && raw !== null) {
            return Object.fromEntries(
                Object.entries(raw).map(([k, v]) => [k, t(`${key}.${k}`, augmentedOptions)])
            );
        }

        const { interpolated, hasTags } = interpolationControl(raw, augmentedOptions);
        const requirements = extractRequirements(interpolated);
        const hasWhatItTakes = hasReplacements(requirements, augmentedOptions);

        if (!hasWhatItTakes || !hasTags) return interpolated;

        if (!ALL_KEYS.has(cleanKey(key))) {
            return (
                <Trans
                    i18nKey='doNotTranslate:INTERPOLATION'
                    values={{
                        INTERPOLATION: fixSpecialCharacters(unescape(convertAkTagsToPascal(key))),
                        ...augmentedOptions,
                    }}
                    components={augmentedOptions}
                    parent={({ children }) => {
                        return ['ja', 'zh'].includes(ApplicationLanguage.value) ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                {parseChildren(children)}
                            </div>
                        ) : (
                            <>{children}</>
                        );
                    }}
                />
            );
        }

        return (
            <Trans i18nKey={interpolated} values={augmentedOptions} components={augmentedOptions} />
        );
    }

    return { t: t as any, i18n, ...rest };
}
